import { applyAppBadge, clearAppBadge } from "./app-badge";

export type ChewNotificationKind = "review-requested" | "changes-requested" | "approved" | "ci-failed" | "comment-added";

export interface ChewNotification {
  id: string;
  kind: ChewNotificationKind;
  repository: string;
  pullNumber: number;
  title: string;
  detail: string;
  href: string;
  occurredAt: string;
  readAt?: string;
}

export interface NotificationCenterSnapshot {
  items: ChewNotification[];
  unreadCount: number;
  ready: boolean;
}

interface PersistedNotificationCenter {
  v: number;
  login: string;
  items: ChewNotification[];
}

const storageKey = "chew:notification-center";
const storageVersion = 1;
const maximumItems = 100;
const listeners = new Set<() => void>();
let activeLogin: string | undefined;
let snapshot: NotificationCenterSnapshot = { items: [], unreadCount: 0, ready: false };
let listeningForStorage = false;

function notificationIsValid(value: unknown): value is ChewNotification {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ChewNotification>;
  return typeof item.id === "string"
    && ["review-requested", "changes-requested", "approved", "ci-failed", "comment-added"].includes(item.kind ?? "")
    && typeof item.repository === "string"
    && typeof item.pullNumber === "number"
    && typeof item.title === "string"
    && typeof item.detail === "string"
    && typeof item.href === "string"
    && typeof item.occurredAt === "string"
    && (item.readAt === undefined || typeof item.readAt === "string");
}

export function parseNotificationCenter(raw: string | null, login: string): ChewNotification[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedNotificationCenter>;
    if (parsed.v !== storageVersion || parsed.login !== login || !Array.isArray(parsed.items)) return [];
    return parsed.items.filter(notificationIsValid).slice(0, maximumItems);
  } catch {
    return [];
  }
}

function persisted(login: string): ChewNotification[] {
  if (typeof window === "undefined") return [];
  return parseNotificationCenter(window.localStorage.getItem(storageKey), login);
}

function publish(items: ChewNotification[], ready = true): void {
  const ordered = [...items]
    .sort((left, right) => Date.parse(right.occurredAt) - Date.parse(left.occurredAt))
    .slice(0, maximumItems);
  snapshot = {
    items: ordered,
    unreadCount: ordered.filter((item) => !item.readAt).length,
    ready,
  };
  applyAppBadge(snapshot.unreadCount);
  listeners.forEach((listener) => listener());
}

function write(items: ChewNotification[]): void {
  if (!activeLogin || typeof window === "undefined") return;
  window.localStorage.setItem(storageKey, JSON.stringify({
    v: storageVersion,
    login: activeLogin,
    items,
  } satisfies PersistedNotificationCenter));
  publish(items);
}

function listenForStorage(): void {
  if (listeningForStorage || typeof window === "undefined") return;
  listeningForStorage = true;
  window.addEventListener("storage", (event) => {
    if (event.key !== storageKey || !activeLogin) return;
    publish(parseNotificationCenter(event.newValue, activeLogin));
  });
}

export function activateNotificationCenter(login: string): NotificationCenterSnapshot {
  listenForStorage();
  if (activeLogin !== login || !snapshot.ready) {
    activeLogin = login;
    publish(persisted(login));
  }
  return snapshot;
}

export function appendNotifications(login: string, incoming: ChewNotification[]): ChewNotification[] {
  activateNotificationCenter(login);
  const existing = new Map(snapshot.items.map((item) => [item.id, item]));
  const added: ChewNotification[] = [];
  for (const item of incoming) {
    if (existing.has(item.id)) continue;
    existing.set(item.id, item);
    added.push(item);
  }
  if (added.length > 0) write([...existing.values()]);
  return added;
}

export function markNotificationRead(id: string, readAt = new Date().toISOString()): void {
  if (!activeLogin) return;
  write(snapshot.items.map((item) => item.id === id && !item.readAt ? { ...item, readAt } : item));
}

export function markAllNotificationsRead(readAt = new Date().toISOString()): void {
  if (!activeLogin || snapshot.unreadCount === 0) return;
  write(snapshot.items.map((item) => item.readAt ? item : { ...item, readAt }));
}

export function dismissNotification(id: string): void {
  if (!activeLogin) return;
  write(snapshot.items.filter((item) => item.id !== id));
}

export function clearNotificationCenter(): void {
  activeLogin = undefined;
  snapshot = { items: [], unreadCount: 0, ready: false };
  clearAppBadge();
  listeners.forEach((listener) => listener());
}

export function getNotificationCenterSnapshot(): NotificationCenterSnapshot {
  return snapshot;
}

export function subscribeNotificationCenter(listener: () => void): () => void {
  listenForStorage();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function resetNotificationCenterForTests(): void {
  activeLogin = undefined;
  snapshot = { items: [], unreadCount: 0, ready: false };
  listeners.clear();
}
